import { Visibility as VisibilityOnIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import { Alert, Box, Button, IconButton, InputAdornment, LinearProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSelector from 'components/selectors/LanguageSelector';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useLoginMutation, apiSlice, useLogoutMutation } from 'features/api/apiSlice';
import { localLogout, selectCurrentUser, setCredentials } from 'features/auth/authSlice';
import useProvideClient from 'hooks/client/useProvideClient';
import { Link as RouterLink } from 'react-router-dom';
import { Link } from '@mui/material';
import { UserRole } from 'models/IUser';

export default function LoginPage() {
  const clientProvider = useProvideClient();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const [login] = useLoginMutation();
  const navigate = useNavigate();
  const authUser = useAppSelector(selectCurrentUser);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [isLoggingIn, setIsLogging] = useState(false);
  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);

  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const state = searchParams.get('state');

  const [logout] = useLogoutMutation();

  useEffect(() => {
    const shouldLogout = authUser?.role === UserRole.Patient && (!code || !state);
    if (shouldLogout) {
      logout().unwrap()
        .then(() => {
          dispatch(localLogout());
          dispatch(apiSlice.util.resetApiState());
          setError(t('patients_not_allowed', 'Patients are not allowed to use the web application.')!!);
          setShowError(true);
        })
        .catch(console.error);
    }
  }, [authUser, code, state, dispatch, logout]);

  useEffect(() => {
    if (authUser?.role === UserRole.Patient && code && state) {
      navigate("/fitbit?code=" + code + "&state=" + state);
    } else if (authUser && authUser.role !== UserRole.Patient) {
      navigate("/patients");
    }
  }, [authUser, code, state, navigate]);

  const handleEmailChange: (event: any) => void = (event) => {
    setEmail(event.target.value);
  };

  const handlePasswordChange: (event: any) => void = (event) => {
    setPassword(event.target.value);
  };

  const handleClickShowPassword: () => void = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword: (event: any) => void = (event) => {
    event.preventDefault();
  };

  const handleSubmit: (event: any) => void = async e => {
    e.preventDefault()

    setIsLogging(true)
    await login({email, password})
    .unwrap()
    .then((result) => {
      setIsLogging(false)
      setShowError(false)
      dispatch(apiSlice.util.resetApiState())
      dispatch(setCredentials(result.data))
    })
    .catch((error) => {
      setIsLogging(false);
      setError(t('wrong_email_password', 'Wrong email or password.')!!);
      setShowError(true);
    })
  };

  return (
    <Grid container component="main" sx={{ height: '100vh' }}>
      <Grid
        item
        xs={false}
        sm={4}
        md={6}
        sx={{
          backgroundImage: `url("data:image/svg+xml;base64,${btoa(clientProvider.client.backgroundLoginLeft)}")`,
          backgroundRepeat: 'repeat',
          backgroundSize: 'inherit',
          backgroundPosition: 'center'
        }}
      />
      <Grid item xs={12} sm={8} md={6} component={Paper} elevation={6} square sx={{ display: 'flex', alignContent: 'center', alignItems: 'center' }}>
        <Stack sx={{ position: "fixed", top: '2%', right: '1%' }}>
          <LanguageSelector />
        </Stack>
        <Stack
          width='100%'
          sx={{
            my: 8,
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Box component="img" src={clientProvider.client.logo} maxWidth='30%' sx={{ my: 2 }} />

          {isLoggingIn ?
            <LinearProgress sx={{ width: "60%" }} />
            :
            <>
              <Typography component="h1" variant="h6" color="#b8bacc">
              {code && state 
                ? t("enter_email_and_password_fitbit", "Enter email and password to complete your Fitbit device registration.") 
                : t("enter_email_and_password", "Enter email and password for access.")}
              </Typography>
              <Stack component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1, height: '100%', width: '40%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <TextField
                  variant='standard'
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label={t("email", "Email")}
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={handleEmailChange}
                />

                <TextField
                  variant='standard'
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label={t("password", "Password")}
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={handlePasswordChange}
                  InputProps={{
                    endAdornment:
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityOnIcon />}
                        </IconButton>
                      </InputAdornment>
                  }}
                />

                {showError &&
                  <Alert sx={{ width: '100%', my: 2 }} severity="error">
                    {error}
                  </Alert>
                }

                <Button
                  type="submit"
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={!email || !password}
                >
                  {t("login", "Login")}
                </Button>

                <Link variant="h6" component={RouterLink} to="/forgot-password" color="#b8bacc">
                  {t("forgot_password", "Forgot password?")}
                </Link>
                <Typography variant="body2" align="justify" sx={{ mt: 2 }}>
                  {t("tos_pas_warning", "By accessing and using exCARE, you acknowledge and agree that you have fully read and understood our Terms of Service & Privacy and Security Policy. Your continued use of the application constitutes your agreement to ")}
                  <Link href="/docs/exCARE-ToS-PaS-End-User_EN.pdf" underline="always" target="_blank">
                    {t("terms_and_conditions", "terms and conditions")}.
                  </Link>
                </Typography>
              </Stack>
            </>
          }
        </Stack>
      </Grid>
    </Grid>
  );
}